import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Search, X } from 'lucide-react';
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_META,
  TASK_SORTS,
  TASK_SORT_LABELS,
  TASK_STATUSES,
  TASK_STATUS_META,
  REVIEW_STATUSES,
  type TaskSort,
} from '@ssn/shared';
import { useReferenceData, useTaskList } from '@/lib/queries';
import { useSession } from '@/session/SessionProvider';
import { PageHeader, Panel } from '@/components/ui/Panel';
import { Button, LinkButton } from '@/components/ui/Button';
import { Checkbox, Select, TextInput } from '@/components/ui/Form';
import { Badge } from '@/components/ui/Badge';
import { ErrorState } from '@/components/ui/Feedback';
import { Pagination } from '@/components/ui/Pagination';
import { TaskTable } from '@/components/domain/TaskTable';
import { cn } from '@/components/ui/cn';

const REVIEW_STATUS_LABELS: Record<string, string> = {
  ANY: 'Any review status',
  AWAITING_REVIEW: 'Awaiting final review',
  CHANGES_REQUESTED: 'Changes requested',
  APPROVED: 'Approved',
  NEVER_REVIEWED: 'Never reviewed',
};

/**
 * The task list (§16).
 *
 * Every filter lives in the URL, so a director can construct "all HIGH priority
 * tasks assigned to Alex that are awaiting final review" and paste that view to
 * somebody else — which is the actual point of combinable filters.
 */
export function TaskListPage({ scope }: { scope: 'ALL' | 'MINE' }) {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(params.get('q') ?? '');

  const reference = useReferenceData();

  const query = useMemo(() => {
    const built: Record<string, unknown> = {
      view: scope,
      page: Number(params.get('page') ?? '1'),
      pageSize: 25,
      sort: params.get('sort') ?? 'RECENTLY_UPDATED',
    };

    for (const key of ['q', 'status', 'priority', 'assigneeId', 'creatorId', 'projectId', 'categoryId', 'tag', 'reviewStatus']) {
      const value = params.get(key);
      if (value) built[key] = value;
    }
    for (const key of ['overdueOnly', 'unassignedOnly', 'includeArchived', 'includeDeleted']) {
      if (params.get(key) === 'true') built[key] = true;
    }
    for (const key of [
      'createdFrom',
      'createdTo',
      'updatedFrom',
      'updatedTo',
      'deadlineFrom',
      'deadlineTo',
      'completedFrom',
      'completedTo',
    ]) {
      const value = params.get(key);
      if (value) built[key] = new Date(value).toISOString();
    }

    return built;
  }, [params, scope]);

  const { data, isPending, isFetching, error, refetch } = useTaskList(query);

  const update = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    // Any filter change invalidates the current page number.
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const activeFilterKeys = [...params.keys()].filter(
    (key) => !['page', 'sort', 'q'].includes(key) && params.get(key),
  );

  const clearAll = () => {
    const next = new URLSearchParams();
    const sort = params.get('sort');
    if (sort) next.set('sort', sort);
    setSearchDraft('');
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        title={scope === 'MINE' ? 'My Tasks' : 'All Tasks'}
        description={
          scope === 'MINE'
            ? 'Everything you created or are assigned to.'
            : 'Every task you have access to across the development organisation.'
        }
        actions={can('task:create') ? <LinkButton to="/tasks/new" variant="primary">Create Task</LinkButton> : undefined}
      />

      <Panel flush className="mb-5">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <form
            className="flex min-w-0 flex-1 items-center gap-2"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              update('q', searchDraft.trim() || null);
            }}
          >
            <div className="relative min-w-0 flex-1">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-text-subtle"
              />
              <TextInput
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                placeholder="Search titles, descriptions and task numbers…"
                aria-label="Search tasks"
                className="pl-8"
              />
            </div>
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </form>

          <Select
            aria-label="Sort tasks"
            value={String(query['sort'])}
            onChange={(event) => update('sort', event.target.value)}
            options={TASK_SORTS.map((sort: TaskSort) => ({ value: sort, label: TASK_SORT_LABELS[sort] }))}
            className="w-auto min-w-[11rem]"
          />

          <Button
            variant={activeFilterKeys.length > 0 ? 'primary' : 'secondary'}
            onClick={() => setFiltersOpen((open) => !open)}
            icon={<Filter aria-hidden className="size-4" />}
          >
            Filters
            {activeFilterKeys.length > 0 && (
              <span className="rounded bg-white/20 px-1.5 text-[11px] tabular">{activeFilterKeys.length}</span>
            )}
          </Button>
        </div>

        {filtersOpen && (
          <div className="grid gap-3 border-b border-border bg-surface-sunken p-3 sm:grid-cols-2 lg:grid-cols-4">
            <Select
              label="Status"
              value={params.get('status') ?? ''}
              onChange={(event) => update('status', event.target.value)}
              placeholder="Any status"
              options={TASK_STATUSES.map((status) => ({ value: status, label: TASK_STATUS_META[status].label }))}
            />
            <Select
              label="Priority"
              value={params.get('priority') ?? ''}
              onChange={(event) => update('priority', event.target.value)}
              placeholder="Any priority"
              options={TASK_PRIORITIES.map((priority) => ({
                value: priority,
                label: TASK_PRIORITY_META[priority].label,
              }))}
            />
            <Select
              label="Developer"
              value={params.get('assigneeId') ?? ''}
              onChange={(event) => update('assigneeId', event.target.value)}
              placeholder="Anyone"
              options={(reference.data?.assignableUsers ?? []).map((user) => ({
                value: user.id,
                label: user.displayName,
              }))}
            />
            <Select
              label="Review status"
              value={params.get('reviewStatus') ?? ''}
              onChange={(event) => update('reviewStatus', event.target.value)}
              placeholder="Any review status"
              options={REVIEW_STATUSES.map((status) => ({
                value: status,
                label: REVIEW_STATUS_LABELS[status] ?? status,
              }))}
            />
            <Select
              label="Project"
              value={params.get('projectId') ?? ''}
              onChange={(event) => update('projectId', event.target.value)}
              placeholder="Any project"
              options={(reference.data?.projects ?? []).map((project) => ({
                value: project.id,
                label: project.name,
              }))}
            />
            <Select
              label="Category"
              value={params.get('categoryId') ?? ''}
              onChange={(event) => update('categoryId', event.target.value)}
              placeholder="Any category"
              options={(reference.data?.categories ?? []).map((category) => ({
                value: category.id,
                label: category.name,
              }))}
            />
            {/* §16 lists Created, Updated, Deadline and Completion Date as filters,
                each a range — every one gets both endpoints. */}
            <DateRangeFilter label="Created" fromKey="createdFrom" toKey="createdTo" params={params} onChange={update} />
            <DateRangeFilter label="Updated" fromKey="updatedFrom" toKey="updatedTo" params={params} onChange={update} />
            <DateRangeFilter label="Deadline" fromKey="deadlineFrom" toKey="deadlineTo" params={params} onChange={update} />
            <DateRangeFilter
              label="Completed"
              fromKey="completedFrom"
              toKey="completedTo"
              params={params}
              onChange={update}
            />

            <div className="space-y-2 sm:col-span-2 lg:col-span-4">
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <Checkbox
                  label="Overdue only"
                  checked={params.get('overdueOnly') === 'true'}
                  onChange={(event) => update('overdueOnly', event.target.checked ? 'true' : null)}
                />
                <Checkbox
                  label="Unassigned only"
                  checked={params.get('unassignedOnly') === 'true'}
                  onChange={(event) => update('unassignedOnly', event.target.checked ? 'true' : null)}
                />
                <Checkbox
                  label="Include archived"
                  checked={params.get('includeArchived') === 'true'}
                  onChange={(event) => update('includeArchived', event.target.checked ? 'true' : null)}
                />
                {can('task:read:deleted') && (
                  <Checkbox
                    label="Include deleted"
                    hint="Soft-deleted tasks are retained for the record (§25)."
                    checked={params.get('includeDeleted') === 'true'}
                    onChange={(event) => update('includeDeleted', event.target.checked ? 'true' : null)}
                  />
                )}
              </div>

              {activeFilterKeys.length > 0 && (
                <Button size="sm" variant="ghost" onClick={clearAll} icon={<X aria-hidden className="size-3.5" />}>
                  Clear all filters
                </Button>
              )}
            </div>
          </div>
        )}

        {data && data.facets.byStatus.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-3 py-2">
            <span className="mr-1 text-[11px] text-text-subtle">In this result:</span>
            {data.facets.byStatus.map((facet) => (
              <button
                key={facet.key}
                type="button"
                onClick={() => update('status', params.get('status') === facet.key ? null : facet.key)}
                className={cn('transition-opacity hover:opacity-80')}
              >
                <Badge
                  tone={params.get('status') === facet.key ? 'accent' : 'neutral'}
                  size="sm"
                >
                  {facet.label} <span className="tabular">{facet.count}</span>
                </Badge>
              </button>
            ))}
          </div>
        )}

        {error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : (
          <>
            <TaskTable
              tasks={data?.items ?? []}
              loading={isPending || isFetching}
              hideAssignee={scope === 'MINE'}
              emptyAction={
                activeFilterKeys.length > 0 ? (
                  <Button size="sm" variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />

            {data && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                totalPages={data.totalPages}
                noun="tasks"
                onPageChange={(page) => update('page', String(page))}
              />
            )}
          </>
        )}
      </Panel>
    </>
  );
}

/** A from/to pair for one of the §16 date filters, rendered as one compact field group. */
function DateRangeFilter({
  label,
  fromKey,
  toKey,
  params,
  onChange,
}: {
  label: string;
  fromKey: string;
  toKey: string;
  params: URLSearchParams;
  onChange: (key: string, value: string | null) => void;
}) {
  return (
    <div className="space-y-1.5">
      <span className="block text-xs font-medium text-text">{label}</span>
      <div className="flex items-center gap-1.5">
        <TextInput
          type="date"
          aria-label={`${label} after`}
          value={params.get(fromKey) ?? ''}
          onChange={(event) => onChange(fromKey, event.target.value)}
        />
        <span className="text-xs text-text-subtle" aria-hidden>
          –
        </span>
        <TextInput
          type="date"
          aria-label={`${label} before`}
          value={params.get(toKey) ?? ''}
          onChange={(event) => onChange(toKey, event.target.value)}
        />
      </div>
    </div>
  );
}
